// printable_homework — a downloadable/emailable PDF worksheet. Presentational:
// the learner (or a parent looking over their shoulder) taps to download. The
// PDF opens in a new tab; completing = viewed.

import { useEffect, type ReactElement } from 'react';

import type { content } from '@fajrarabi/zod-schemas';

import { mediaUrl } from '../media';
import { ArabicText } from './primitives';
import type { ActivityRendererProps } from './types';

export function PrintableHomework({
  data,
  ctx,
  cb,
}: ActivityRendererProps<content.PrintableHomeworkData>): ReactElement {
  const { t } = ctx;
  useEffect(() => {
    cb.onViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const href = mediaUrl(ctx.cdnBase, data.pdf);

  return (
    <div className="mx-auto flex w-full max-w-sm flex-col items-center text-center">
      <div className="grid h-32 w-32 place-items-center rounded-3xl bg-cream-100 text-6xl ring-1 ring-cream-200">
        📄
      </div>
      <h2 className="mt-6 text-2xl font-bold text-burgundy-700">
        <ArabicText text={t(data.titleKey)} ctx={ctx} />
      </h2>
      {data.descriptionKey ? (
        <p className="mt-2 text-ink-700">
          <ArabicText text={t(data.descriptionKey)} ctx={ctx} />
        </p>
      ) : null}

      <a
        href={href ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        aria-disabled={!href}
        className={`mt-7 inline-flex min-h-[56px] items-center gap-2 rounded-full px-8 text-lg font-bold transition-colors ${
          href
            ? 'bg-burgundy-500 text-cream-50 hover:bg-burgundy-600'
            : 'pointer-events-none bg-cream-200 text-ink-500'
        }`}
      >
        ⬇️ {t('content.printable.download')}
      </a>
    </div>
  );
}
